import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useStore } from "react-redux";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@material-ui/core";
import IntlMessages, { IntlGetText } from "util/IntlMessages";
import { setLoader } from "actions/Base";
import {
  setReadOnlyImpactView,
  setSelectedMenuItem,
  setSelectedPrescription,
} from "actions/Management";
import { FolderType } from "constants/Enums";
import { callApi, getPatientFollowUpLastImpactView } from "api/apiList";
import GeneralImpact from "../management/GeneralImpact";
import FirstImpact from "../management/Impact/FirstImpact";
import ImpactViewBridge, { normalizeImpactPrescription } from "./ImpactViewBridge";

const LastImpactViewModal = ({ open, personId, patientLabel, onClose }) => {
  const dispatch = useDispatch();
  const store = useStore();
  const fnSetLoader = (data) => dispatch(setLoader(data));
  const [viewData, setViewData] = useState(null);
  const previousState = useRef(null);

  const resetManagementState = () => {
    document.body.classList.remove("read-only-impact-view-active");
    if (previousState.current) {
      dispatch(setSelectedPrescription(previousState.current.selectedPrescription));
      dispatch(setSelectedMenuItem(previousState.current.selectedMenuItem));
    } else {
      dispatch(setSelectedPrescription(null));
    }
    dispatch(setReadOnlyImpactView(false));
    setViewData(null);
  };

  const handleClose = () => {
    resetManagementState();
    onClose();
  };

  useEffect(() => {
    if (!open) {
      setViewData(null);
      return;
    }
    if (!personId) return;

    const management = store.getState().management;
    previousState.current = {
      selectedPrescription: management.selectedPrescription,
      selectedMenuItem: management.selectedMenuItem,
    };

    setViewData(null);

    callApi(
      () => getPatientFollowUpLastImpactView(personId),
      (data) => {
        const prescription = normalizeImpactPrescription(
          data.prescription ?? data.Prescription
        );
        const folderType = Number(
          data.folderType ?? data.FolderType ?? FolderType.tracing
        );
        const isImpactClosed = data.isImpactClosed ?? data.IsImpactClosed ?? true;

        if (!prescription?.impactId) {
          return;
        }

        setViewData({ prescription, folderType, isImpactClosed });
      },
      fnSetLoader
    );
  }, [open, personId]);

  const titleSuffix =
    viewData && !viewData.isImpactClosed
      ? ` (${IntlGetText("patientFollowUp.openImpact")})`
      : "";
  const title = patientLabel
    ? `${IntlGetText("patientFollowUp.lastImpactView")}${titleSuffix} - ${patientLabel}`
    : `${IntlGetText("patientFollowUp.lastImpactView")}${titleSuffix}`;

  const prescription = viewData?.prescription;
  const isFirstImpact = viewData?.folderType === FolderType.newPatients;
  const impactMenuFolder = isFirstImpact ? FolderType.newPatients : FolderType.tracing;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="xl"
      scroll="paper"
      className="new-patient-dialog"
      disableBackdropClick
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent className="new-patient-dialog-content" style={{ paddingTop: 8 }}>
        {viewData && prescription && (
          <div
            style={{
              maxHeight: "calc(100vh - 180px)",
              overflow: "auto",
              padding: "0 8px 8px",
            }}
          >
            <ImpactViewBridge
              prescription={prescription}
              folderType={impactMenuFolder}
            >
              {isFirstImpact ? (
                <FirstImpact />
              ) : (
                <GeneralImpact menuItemOverride={impactMenuFolder} />
              )}
            </ImpactViewBridge>
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="secondary">
          <IntlMessages id="core.close" />
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LastImpactViewModal;
